import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';

export default function TermsOfServicePage() {
  const { t } = useTranslation();

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AnalizaPrawna', item: 'https://analizaprawna.eu/' },
      { '@type': 'ListItem', position: 2, name: t('legal.termsOfService.title') },
    ],
  };

  return (
    <div className="max-w-4xl mx-auto">
      <SEOHead
        title={`${t('legal.termsOfService.title')} | AnalizaPrawna`}
        description={t('legal.termsOfService.metaDescription')}
        canonicalUrl="https://analizaprawna.eu/regulamin"
        jsonLd={breadcrumbLd}
      />

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 md:p-12">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('legal.termsOfService.title')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
          {t('legal.termsOfService.lastUpdated')}
        </p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          {/* Section 1 - General Provisions */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.general.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.general.p1')}</li>
              <li>{t('legal.termsOfService.sections.general.p2')}</li>
              <li>{t('legal.termsOfService.sections.general.p3')}</li>
              <li>{t('legal.termsOfService.sections.general.p4')}</li>
            </ol>
          </section>

          {/* Section 2 - Service Description */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.serviceDescription.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.serviceDescription.p1')}</li>
              <li>{t('legal.termsOfService.sections.serviceDescription.p2')}</li>
              <li>{t('legal.termsOfService.sections.serviceDescription.p3')}</li>
              <li>{t('legal.termsOfService.sections.serviceDescription.p4')}</li>
            </ol>
          </section>

          {/* Section 3 - User Obligations */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.userObligations.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.userObligations.p1')}</li>
              <li>{t('legal.termsOfService.sections.userObligations.p2')}</li>
              <li>{t('legal.termsOfService.sections.userObligations.p3')}</li>
              <li>{t('legal.termsOfService.sections.userObligations.p4')}</li>
              <li>{t('legal.termsOfService.sections.userObligations.p5')}</li>
            </ol>
          </section>

          {/* Section 4 - Data Anonymization */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.anonymization.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.anonymization.p1')}</li>
              <li>{t('legal.termsOfService.sections.anonymization.p2')}</li>
              <li>{t('legal.termsOfService.sections.anonymization.p3')}</li>
            </ol>
          </section>

          {/* Section 5 - Accounts and Registration */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.accounts.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.accounts.p1')}</li>
              <li>{t('legal.termsOfService.sections.accounts.p2')}</li>
              <li>{t('legal.termsOfService.sections.accounts.p3')}</li>
              <li>{t('legal.termsOfService.sections.accounts.p4')}</li>
            </ol>
          </section>

          {/* Section 6 - Payment Terms */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.payments.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.payments.p1')}</li>
              <li>{t('legal.termsOfService.sections.payments.p2')}</li>
              <li>{t('legal.termsOfService.sections.payments.p3')}</li>
              <li>{t('legal.termsOfService.sections.payments.p4')}</li>
              <li>{t('legal.termsOfService.sections.payments.p5')}</li>
              <li>{t('legal.termsOfService.sections.payments.p6')}</li>
            </ol>
          </section>

          {/* Section 7 - Right of Withdrawal */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.withdrawal.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.withdrawal.p1')}</li>
              <li>{t('legal.termsOfService.sections.withdrawal.p2')}</li>
              <li>{t('legal.termsOfService.sections.withdrawal.p3')}</li>
              <li>{t('legal.termsOfService.sections.withdrawal.p4')}</li>
              <li>{t('legal.termsOfService.sections.withdrawal.p5')}</li>
            </ol>
          </section>

          {/* Section 8 - Complaints */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.complaints.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.complaints.p1')}</li>
              <li>{t('legal.termsOfService.sections.complaints.p2')}</li>
              <li>{t('legal.termsOfService.sections.complaints.p3')}</li>
              <li>{t('legal.termsOfService.sections.complaints.p4')}</li>
              <li>{t('legal.termsOfService.sections.complaints.p5')}</li>
            </ol>
          </section>

          {/* Section 9 - Liability */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.liability.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.liability.p1')}</li>
              <li>{t('legal.termsOfService.sections.liability.p2')}</li>
              <li>{t('legal.termsOfService.sections.liability.p3')}</li>
              <li>{t('legal.termsOfService.sections.liability.p4')}</li>
              <li>{t('legal.termsOfService.sections.liability.p5')}</li>
            </ol>
          </section>

          {/* Section 10 - Intellectual Property */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.intellectualProperty.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.intellectualProperty.p1')}</li>
              <li>{t('legal.termsOfService.sections.intellectualProperty.p2')}</li>
              <li>{t('legal.termsOfService.sections.intellectualProperty.p3')}</li>
            </ol>
          </section>

          {/* Section 11 - Termination */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.termination.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.termination.p1')}</li>
              <li>{t('legal.termsOfService.sections.termination.p2')}</li>
              <li>{t('legal.termsOfService.sections.termination.p3')}</li>
            </ol>
          </section>

          {/* Section 12 - Final Provisions */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.termsOfService.sections.finalProvisions.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.termsOfService.sections.finalProvisions.p1')}</li>
              <li>{t('legal.termsOfService.sections.finalProvisions.p2')}</li>
              <li>{t('legal.termsOfService.sections.finalProvisions.p3')}</li>
              <li>{t('legal.termsOfService.sections.finalProvisions.p4')}</li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
